/**
 * Lab member Net Exposure Details → getBetDetailsForWorkstation
 * (sportsbook) / Artemis bets/getBetDetailsForWorkstation (PB).
 *
 * Lab body: `user` = logged-in profile id, `playerId` = viewed member.
 * Strix SQL: when `playerId` is set, filter is `player.id = playerId`
 * (not `${accessLevel}.id = user`). Sending only `user`=player + caller
 * accessLevel (Triple Master) matched no rows → “No bets found”.
 */

import { useQuery } from '@tanstack/react-query'
import { betFetch, getCallerAgentId, hasLiveApi, hasLiveBet } from '@/api/client'
import { useSession } from '@/api/hooks/shared/useSession'
import { keys } from '@/api/hooks/shared/keys'
import { ROLE_LABEL, type Role } from '@/data/types'
import {
  toWorkstationBet,
  type BetDetailsForWorkstationResponse,
} from '@/api/map/workstation'
import { defaultBetDateRange, type WorkstationBet } from '@/features/net-exposure/workstation-fixture'

function labLevelLabel(role: Role | undefined): string {
  if (!role) return 'Master'
  return ROLE_LABEL[role]
}

export type PlayerMarketBetsQuery = {
  playerId: string
  marketId: string
  /** Prefer username filter when the API returns mixed rows. */
  playerUsername?: string
}

/** Lab `getBetDetailsForWorkstationToDate` body for the member Details modal. */
export function playerMarketBetsRequest(
  query: PlayerMarketBetsQuery,
  callerId: string,
  callerRole: Role | undefined,
) {
  const range = defaultBetDateRange()
  return {
    user: callerId,
    playerId: query.playerId,
    marketId: query.marketId,
    accessLevel: labLevelLabel(callerRole),
    page: 1,
    maxRow: 100,
    fromDate: range.fromDate,
    toDate: range.toDate,
    currencyType: 'All' as const,
    ...(query.playerUsername ? { userName: query.playerUsername } : {}),
  }
}

async function loadPlayerMarketBets(
  query: PlayerMarketBetsQuery,
  callerId: string,
  callerRole: Role | undefined,
): Promise<WorkstationBet[]> {
  if (!hasLiveApi() || !hasLiveBet()) return []
  const res = await betFetch<BetDetailsForWorkstationResponse>(
    '/v1/bets/getBetDetailsForWorkstation',
    {
      method: 'POST',
      body: JSON.stringify(playerMarketBetsRequest(query, callerId, callerRole)),
    },
  )
  return (res.result ?? []).map(toWorkstationBet)
}

export function usePlayerMarketBets(
  query: PlayerMarketBetsQuery | null,
  enabled: boolean,
) {
  const { data: session } = useSession()
  const callerRole = session?.account.role
  const callerId = getCallerAgentId() ?? session?.account.id

  return useQuery({
    queryKey: keys.playerMarketBets(query ?? { playerId: '', marketId: '' }),
    queryFn: () => loadPlayerMarketBets(query!, callerId!, callerRole),
    enabled: Boolean(
      enabled && session && callerId && query?.playerId && query.marketId,
    ),
  })
}
